import { styled } from '@mui/material';
import ActionLink from 'components/ActionLink';
import { ActionRef } from 'further-types/lp/action';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { groupBy, mapValues, round, sumBy } from 'lodash';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import useSyncInitialRefundValues from './hooks/useSyncInitialRefundValues';
import { Column, Divider } from 'further-ui/components/Layout';

const ActionsWrapper = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const actionsColumns = [
  {
    key: 'ledgerDate',
    label: 'Date',
    width: 140,
  },
  {
    key: 'actionRef',
    label: 'Action',
    width: 180,
    render: (row: { id: string; actionRef: ActionRef }) => (
      <ActionLink id={row.id} actionRef={row.actionRef} />
    ),
  },
  {
    key: 'allocatedAmountLcy',
    label: 'Allocated',
    width: 140,
  },
  {
    key: 'paidAmountLcy',
    label: 'Funds paid in',
    width: 140,
  },
  {
    key: 'overpaymentAmountLcy',
    label: 'Overpayment',
    width: 140,
  },
];

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
};

const CapitalRefundDetails: React.FC<Props> = ({
  index,
  vehicleId,
  baseCurrency,
}) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows([]);

  const [overpaidActions] = api.capital.overpaidActions.useSuspenseQuery({
    vehicleId,
  });

  const commitmentValues = mapValues(
    groupBy(overpaidActions, (a) => a.commitmentId),
    (commitments) =>
      round(
        sumBy(
          commitments,
          (commitment) => commitment.totalOverpaymentAmountLcy,
        ),
        2,
      ),
  );

  useSyncInitialRefundValues({
    entryIndex: index,
    overpaidActions,
  });

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
    },
    {
      key: 'totalOverpaymentAmountLcy',
      label: 'Overpayment',
      render: (row) =>
        numberToCurrencyString(row.totalOverpaymentAmountLcy, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'amountToReturnLcy',
      label: 'Amount to return',
      render: (row: { commitmentId: string }) => (
        <NumberInput
          disabled
          value={commitmentValues[row.commitmentId] ?? 0}
          decimals={2}
          placeholder="Enter amount"
          clearZeroValueOnFocus
        />
      ),
    },
  ];
  const rows = overpaidActions.map((action) => ({
    id: action.commitmentId,
    commitmentId: action.commitmentId,
    investorName: action.investorName,
    totalOverpaymentAmountLcy: action.totalOverpaymentAmountLcy,
    actions: action.actions,
  }));

  return (
    <>
      <Divider />
      <Table
        columns={columns}
        rows={rows}
        isExpandableRow={() => true}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        onRowExpand={({ id }) => toggleRowExpansion(id)}
        onRowClick={({ id }) => toggleRowExpansion(id)}
        emptyMessage="No capital to return."
        getExpandedContent={(row) => (
          <ActionsWrapper>
            <Table
              showTopBorder
              scrollable
              showHeaderBorder
              columns={actionsColumns}
              rows={row.actions.map((action) => ({
                id: action.id,
                ledgerDate: action.ledgerDate.toLabel(),
                actionRef: action.actionRef,
                allocatedAmountLcy: numberToCurrencyString(
                  action.allocatedAmountLcy,
                  { currency: baseCurrency },
                ),
                paidAmountLcy: numberToCurrencyString(action.paidAmountLcy, {
                  currency: baseCurrency,
                }),
                overpaymentAmountLcy: numberToCurrencyString(
                  action.overpaymentAmountLcy,
                  { currency: baseCurrency },
                ),
              }))}
            />
          </ActionsWrapper>
        )}
      />
    </>
  );
};

export default CapitalRefundDetails;
